// Rooms that are gone (03-startup-session.md): a room the chat server no longer knows (deleted and
// purged: the server never sends its leave) is dropped from the client and kept in this account's
// list on this device, so its stored copy never shows it again.
import { ClientEvent, KnownMembership, type MatrixClient } from 'matrix-js-sdk';
import { navigate } from '../core/router';
import { readJson, writeJson } from '../core/storage';
import { refFor } from './rooms';

const key = (client: MatrixClient) => `desyr_gone_rooms:${client.getUserId()}`;

/** Drops rooms, and all known to be gone, from the client and remembers them (its 500 newest); an open page of one goes Home. */
export function dropRooms(client: MatrixClient, ids: string[]): void {
  const gone = [...new Set([...ids, ...(readJson<string[]>(key(client)) ?? [])])].slice(0, 500);
  writeJson(key(client), gone);
  for (const id of gone) {
    const room = client.getRoom(id);
    if (!room) continue;
    if (location.pathname.includes(encodeURIComponent(refFor(room)))) navigate('/home/', { replace: true });
    client.store.removeRoom(id);
    client.emit(ClientEvent.DeleteRoom, id);
  }
}

/** At every start, before the app opens: the rooms known to be gone leave the stored copy again. */
export const dropKnownGone = (client: MatrixClient) => dropRooms(client, []);

/** Once the app is open: the joined rooms the server no longer holds are gone (no toast; a failure changes nothing). */
export async function checkJoined(client: MatrixClient): Promise<void> {
  const held = new Set((await client.getJoinedRooms().catch(() => undefined))?.joined_rooms ?? client.getRooms().map((r) => r.roomId));
  dropRooms(client, client.getRooms().filter((r) => r.getMyMembership() === KnownMembership.Join && !held.has(r.roomId)).map((r) => r.roomId));
}

/** Whether the server still holds the person in this room (for a deletion that answered "not there"). */
export const stillJoined = async (client: MatrixClient, id: string) => (await client.getJoinedRooms()).joined_rooms.includes(id);
